// Browser page translation (Chrome/Google Translate) replaces the text nodes
// React owns with its own <font> elements. React's next commit then calls
// removeChild/insertBefore on a node whose parent changed, the browser throws
// NotFoundError, and the guest lands on the error page. samera-one logged this
// several hundred times a week (samera/samera#3591).
//
// The fix is the community workaround from
// https://github.com/facebook/react/issues/11538 (checked 2026-09-24): when the
// node is no longer a child of the expected parent, skip the call instead of
// throwing. Chromium tracks the cause as https://issues.chromium.org/issues/41407169.
//
// Known limits on a translated page:
// - React keeps updating the original text node, which is no longer on screen,
//   so translated text can stay stale. Prices opt out of translation for this
//   reason (`translate="no"` in the Price component).
// - A skipped removeChild leaves the translated copy on screen, so text React
//   removed can stay visible until reload.
// - A skipped insertBefore means that element stays missing until reload.
//
// The patch is global, so it also tolerates the same mismatch from non-React
// code. Tolerated calls are reported as a WARN, but only once per operation and
// route, within the per-session cap, and only where a collector exists.
import { logWarning } from '~shared/observability/observability';

type Operation = 'removeChild' | 'insertBefore';

const report = (operation: Operation, movedNode: Node): void => {
  // The operation is in the message so each one gets its own dedupe key.
  logWarning(new Error(`DOM node moved outside React (${operation})`), {
    kind: 'dom-foreign-mutation',
    'dom.operation': operation,
    'dom.moved_to': movedNode.parentNode?.nodeName ?? '(detached)',
  });
};

export const installDomTranslationGuard = (): (() => void) => {
  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      report('removeChild', child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    ref: Node | null,
  ): T {
    if (ref && ref.parentNode !== this) {
      // gstack-shortcut(dec-87f14921): skipped insert can hide an element until reload, upgrade when SigNoz shows insertBefore WARN records in more than a handful of sessions per week
      report('insertBefore', ref);
      return node;
    }
    return originalInsertBefore.call(this, node, ref) as T;
  };

  return () => {
    Node.prototype.removeChild = originalRemoveChild;
    Node.prototype.insertBefore = originalInsertBefore;
  };
};

// Importing the module installs the guard, like instrument.ts. It must run
// before React's first commit.
if (typeof Node !== 'undefined') installDomTranslationGuard();
